// DEV-ONLY HUD panel for spawning dummy enemies and editing own ship during local testing.
// Gated by VITE_ENABLE_DUMMY_SPAWNER=true; otherwise the component renders null.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { networkManager } from '../../systems/NetworkManager';
import { onGameEvent } from '../../core/GameEvents';
import { SHIPS, WEAPONS, ENHANCERS, UTILITIES, MINING_TOOLS, WORLD_CONFIG, ALL_EQUIPMENT } from '@astra/shared';

const ENABLED = import.meta.env.VITE_ENABLE_DUMMY_SPAWNER === 'true';

const SHIP_CLASSES = ['corvette', 'frigate', 'destroyer', 'cruiser', 'battleship', 'carrier', 'dreadnought', 'hauler'] as const;

// --- Equipment option lists ---

const ALL_WEAPON_OPTIONS = Object.values(WEAPONS)
  .sort((a, b) => a.weaponType.localeCompare(b.weaponType) || a.tier - b.tier);

const ALL_ENHANCER_OPTIONS = Object.values(ENHANCERS)
  .sort((a, b) => a.enhancerType.localeCompare(b.enhancerType) || a.tier - b.tier);

// Combat-relevant enhancers for dummy spawner (hull, boost, heat, shield amp)
const COMBAT_ENHANCER_OPTIONS = ALL_ENHANCER_OPTIONS
  .filter((u) => ['hull_plating', 'booster', 'heat_dissipator', 'shield_amplifier'].includes(u.enhancerType));

const ACTUAL_UTILITY_OPTIONS = Object.values(UTILITIES)
  .sort((a, b) => a.utilityType.localeCompare(b.utilityType) || a.tier - b.tier);

const TOOL_OPTIONS = Object.values(MINING_TOOLS)
  .sort((a, b) => a.name.localeCompare(b.name) || a.tier - b.tier);

// --- Label helpers ---

function enhancerLabel(u: typeof ALL_ENHANCER_OPTIONS[number]): string {
  const s = u.stats;
  if (s.hullBonus) return `+${s.hullBonus}hull`;
  if (s.droneCapacity) return `${s.droneCapacity}drones`;
  if (s.boostSpeedMultiplier) return `x${s.boostSpeedMultiplier}boost`;
  if (s.cargoBonus) return `+${s.cargoBonus}cargo`;
  if (s.sensorBonus) return `+${s.sensorBonus}sensor`;
  if (s.heatCapacityBonus) return `+${s.heatCapacityBonus}heat +${s.heatDissipationBonus}diss`;
  if (s.shieldRadiusBonus) return `+${s.shieldRadiusBonus}radius`;
  return '';
}

function utilityLabel(u: typeof ACTUAL_UTILITY_OPTIONS[number]): string {
  const s = u.stats;
  if (s.scanRange) return `${s.scanRange}rng`;
  if (s.beaconTier) return `T${s.beaconTier} beacon`;
  return '';
}

function weaponLabel(w: typeof ALL_WEAPON_OPTIONS[number]): string {
  return `${w.stats.damage}dmg ${w.stats.range}rng`;
}

// --- Shared styles ---

const sel: React.CSSProperties = {
  width: '100%',
  background: '#1a1208',
  color: '#e8c98a',
  border: '1px solid #6a3f1a',
  padding: '2px 4px',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 10,
  marginBottom: 2,
};

const lbl: React.CSSProperties = {
  fontSize: 9,
  color: '#887755',
  marginBottom: 1,
  textTransform: 'uppercase',
  letterSpacing: 1,
};

function btnStyle(bg: string): React.CSSProperties {
  return {
    flex: 1,
    background: bg,
    color: '#fff',
    border: '1px solid #6a3f1a',
    padding: '4px 6px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    cursor: 'pointer',
  };
}

// --- Ship class/tier selector (shared between tabs) ---

function useShipSelector() {
  const shipsByClassTier = useMemo(() => {
    const map = new Map<string, Map<number, string>>();
    for (const ship of Object.values(SHIPS)) {
      if (!map.has(ship.class)) map.set(ship.class, new Map());
      map.get(ship.class)!.set(ship.tier, ship.id);
    }
    return map;
  }, []);

  const [shipClass, setShipClass] = useState<string>('corvette');
  const [tier, setTier] = useState<number>(1);

  const availableTiers = useMemo(() => {
    const tiers = shipsByClassTier.get(shipClass);
    return tiers ? [...tiers.keys()].sort() : [1];
  }, [shipClass, shipsByClassTier]);

  useEffect(() => {
    if (!availableTiers.includes(tier)) setTier(availableTiers[0]);
  }, [availableTiers, tier]);

  const shipId = shipsByClassTier.get(shipClass)?.get(tier) ?? 'corvette_t1';

  return { shipClass, setShipClass, tier, setTier, availableTiers, shipId };
}

function ShipSelector({ shipClass, setShipClass, tier, setTier, availableTiers }: {
  shipClass: string; setShipClass: (v: string) => void;
  tier: number; setTier: (v: number) => void;
  availableTiers: number[];
}) {
  return (
    <>
      <div style={lbl}>Ship</div>
      <div style={{ display: 'flex', gap: 3, marginBottom: 2 }}>
        <select value={shipClass} onChange={(e) => setShipClass(e.target.value)} style={{ ...sel, flex: 1, marginBottom: 0 }}>
          {SHIP_CLASSES.map((c) => (
            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
          ))}
        </select>
        <select value={tier} onChange={(e) => setTier(Number(e.target.value))} style={{ ...sel, flex: 'none', width: 42, marginBottom: 0 }}>
          {availableTiers.map((t) => (
            <option key={t} value={t}>T{t}</option>
          ))}
        </select>
      </div>
    </>
  );
}

// ==================== TAB 1: Dummy Spawner ====================

function DummySpawnerTab() {
  const ship = useShipSelector();
  const [weaponId, setWeaponId] = useState<string>('weapon_kinetic_t1');
  const [enhancer1, setEnhancer1] = useState<string>('');
  const [enhancer2, setEnhancer2] = useState<string>('');
  const [utilityId, setUtilityId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [dummyCount, setDummyCount] = useState(0);

  useEffect(() => {
    return onGameEvent('dummyCountChanged', ({ count }) => setDummyCount(count));
  }, []);

  const spawnLockedRef = useRef(false);
  const [spawnDisabled, setSpawnDisabled] = useState(false);

  const spawn = (mode: 'passive' | 'hostile' | 'friendly') => {
    if (spawnLockedRef.current) return;
    spawnLockedRef.current = true;
    setSpawnDisabled(true);
    setTimeout(() => { spawnLockedRef.current = false; setSpawnDisabled(false); }, 3000);
    const utilityIds = [enhancer1, enhancer2, utilityId].filter(Boolean);
    networkManager.sendSpawnDummyEnemy(ship.shipId, mode, {
      weaponId: weaponId || undefined,
      utilityIds: utilityIds.length > 0 ? utilityIds : undefined,
    });
    setStatus(`Spawning ${mode}…`);
    setTimeout(() => setStatus(''), 1500);
  };

  const despawn = () => {
    networkManager.sendDespawnDummyEnemies();
    setDummyCount(0);
    setStatus('Despawning all…');
    setTimeout(() => setStatus(''), 1500);
  };

  const enhancerSelect = (value: string, onChange: (v: string) => void) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={sel}>
      <option value="">None</option>
      {COMBAT_ENHANCER_OPTIONS.map((u) => (
        <option key={u.id} value={u.id}>T{u.tier} {u.name} [{u.size}] [{u.power}pw]</option>
      ))}
    </select>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
        <span style={{ fontSize: 10, color: dummyCount > 0 ? '#ff5555' : '#666' }}>Active: {dummyCount}</span>
      </div>

      <ShipSelector {...ship} />

      <div style={lbl}>Weapon</div>
      <select value={weaponId} onChange={(e) => setWeaponId(e.target.value)} style={sel}>
        {ALL_WEAPON_OPTIONS.map((w) => (
          <option key={w.id} value={w.id}>T{w.tier} {w.name} [{w.size}] [{w.power}pw]</option>
        ))}
      </select>

      <div style={lbl}>Enhancer 1</div>
      {enhancerSelect(enhancer1, setEnhancer1)}
      <div style={lbl}>Enhancer 2</div>
      {enhancerSelect(enhancer2, setEnhancer2)}

      <div style={lbl}>Utility</div>
      <select value={utilityId} onChange={(e) => setUtilityId(e.target.value)} style={sel}>
        <option value="">None</option>
        {ACTUAL_UTILITY_OPTIONS.map((u) => (
          <option key={u.id} value={u.id}>T{u.tier} {u.name} [{u.size}] [{u.power}pw]</option>
        ))}
      </select>

      <div style={{ display: 'flex', gap: 4, marginBottom: 4, marginTop: 4 }}>
        <button onClick={() => spawn('passive')} disabled={spawnDisabled} style={{ ...btnStyle('#444'), opacity: spawnDisabled ? 0.4 : 1 }}>
          Passive
        </button>
        <button onClick={() => spawn('hostile')} disabled={spawnDisabled} style={{ ...btnStyle('#aa3322'), opacity: spawnDisabled ? 0.4 : 1 }}>
          Hostile
        </button>
        <button onClick={() => spawn('friendly')} disabled={spawnDisabled} style={{ ...btnStyle('#2266aa'), opacity: spawnDisabled ? 0.4 : 1 }}>
          Friendly
        </button>
      </div>
      <button onClick={despawn} style={{ ...btnStyle('#333'), width: '100%' }}>
        Despawn All
      </button>
      {status && (
        <div style={{ marginTop: 3, fontSize: 9, color: '#aaa' }}>{status}</div>
      )}
    </>
  );
}

// ==================== TAB 2: My Ship Editor ====================

// Get equipment options for a given slot type, filtered by slot size
const SIZE_ORDER: string[] = ['SMALL', 'MEDIUM', 'LARGE'];
function fitsSlot(equipSize: string, slotSize: string): boolean {
  return SIZE_ORDER.indexOf(equipSize) <= SIZE_ORDER.indexOf(slotSize);
}

function getOptionsForSlotType(slotType: string, slotSize: string): Array<{ id: string; label: string }> {
  switch (slotType) {
    case 'WEAPON':
      return ALL_WEAPON_OPTIONS
        .filter((w) => fitsSlot(w.size, slotSize))
        .map((w) => ({
          id: w.id,
          label: `T${w.tier} ${w.name} [${w.size}] [${w.power}pw]`,
        }));
    case 'ENHANCER':
      return ALL_ENHANCER_OPTIONS
        .filter((u) => fitsSlot(u.size, slotSize))
        .map((u) => ({
          id: u.id,
          label: `T${u.tier} ${u.name} [${u.size}] [${u.power}pw]`,
        }));
    case 'UTILITY':
      return ACTUAL_UTILITY_OPTIONS
        .filter((u) => fitsSlot(u.size, slotSize))
        .map((u) => ({
          id: u.id,
          label: `T${u.tier} ${u.name} [${u.size}] [${u.power}pw]`,
        }));
    case 'TOOL':
      return TOOL_OPTIONS
        .filter((t) => fitsSlot(t.size, slotSize))
        .map((t) => ({
          id: t.id,
          label: `T${t.tier} ${t.name} [${t.size}] [${t.power}pw]`,
        }));
    default:
      return [];
  }
}

function MyShipTab() {
  const ship = useShipSelector();
  const [slotEquipment, setSlotEquipment] = useState<Record<number, string>>({});
  const [status, setStatus] = useState<string>('');
  const [initialized, setInitialized] = useState(false);

  // Pre-fill with current player ship and equipment on first mount
  useEffect(() => {
    if (initialized) return;
    const ps = networkManager.getDevPlayerState();
    if (!ps) return;
    const currentShipDef = SHIPS[ps.shipClassId];
    if (currentShipDef) {
      ship.setShipClass(currentShipDef.class);
      ship.setTier(currentShipDef.tier);
    }
    const eq: Record<number, string> = {};
    ps.slots.forEach((slot, i) => {
      if (slot.equipped) eq[i] = slot.equipped;
    });
    setSlotEquipment(eq);
    setInitialized(true);
  }, [initialized]);

  // Get the slot definitions for the selected ship
  const shipDef = SHIPS[ship.shipId];
  const slots = shipDef?.slots ?? [];

  // Reset slot equipment when ship changes (but not on initial pre-fill)
  const prevShipIdRef = useRef(ship.shipId);
  useEffect(() => {
    if (prevShipIdRef.current !== ship.shipId) {
      setSlotEquipment({});
      prevShipIdRef.current = ship.shipId;
    }
  }, [ship.shipId]);

  const apply = () => {
    // Build equipment map: only include slots with equipment selected
    const equipment: Record<number, string> = {};
    for (const [idx, eqId] of Object.entries(slotEquipment)) {
      if (eqId) equipment[Number(idx)] = eqId;
    }
    networkManager.sendDevChangeShip(ship.shipId, equipment);
    setStatus('Applied!');
    setTimeout(() => setStatus(''), 1500);
  };

  const setSlot = (index: number, value: string) => {
    setSlotEquipment((prev) => ({ ...prev, [index]: value }));
  };

  // Compute total power draw from selected equipment
  const { powerUsed, powerCap } = useMemo(() => {
    const cap = shipDef?.stats.powerCapacity ?? 0;
    let used = 0;
    for (const eqId of Object.values(slotEquipment)) {
      if (eqId && ALL_EQUIPMENT[eqId]) used += ALL_EQUIPMENT[eqId].power;
    }
    return { powerUsed: used, powerCap: cap };
  }, [slotEquipment, shipDef]);

  const overPower = powerUsed > powerCap;

  // Group slots by type for cleaner display, preserving original indices
  const slotsByType = useMemo(() => {
    const groups: Array<{ type: string; slots: Array<{ index: number; size: string }> }> = [];
    const typeMap = new Map<string, Array<{ index: number; size: string }>>();

    slots.forEach((slot, index) => {
      if (!typeMap.has(slot.type)) {
        typeMap.set(slot.type, []);
        groups.push({ type: slot.type, slots: typeMap.get(slot.type)! });
      }
      typeMap.get(slot.type)!.push({ index, size: slot.size });
    });
    return groups;
  }, [slots]);

  return (
    <>
      <ShipSelector {...ship} />

      {shipDef && (
        <>
          <div style={{ fontSize: 9, color: '#666', marginBottom: 2 }}>
            {shipDef.name} — hull:{shipDef.stats.hull} heat:{shipDef.stats.heatCapacity} mass:{shipDef.stats.mass}
          </div>
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, marginBottom: 1 }}>
              <span style={{ color: overPower ? '#ff4444' : '#e8c98a' }}>
                POWER {powerUsed}/{powerCap}
              </span>
              {overPower && <span style={{ color: '#ff4444' }}>OVER!</span>}
            </div>
            <div style={{ width: '100%', height: 4, background: '#1a1208', border: '1px solid #6a3f1a', borderRadius: 2 }}>
              <div style={{
                width: `${Math.min(100, powerCap > 0 ? (powerUsed / powerCap) * 100 : 0)}%`,
                height: '100%',
                background: overPower ? '#ff4444' : powerUsed / powerCap > 0.8 ? '#ffaa44' : '#44aa44',
                borderRadius: 1,
                transition: 'width 0.15s, background 0.15s',
              }} />
            </div>
          </div>
        </>
      )}

      {slotsByType.map(({ type, slots: typeSlots }) =>
        typeSlots.map(({ index, size }, i) => {
          const options = getOptionsForSlotType(type, size);
          return (
            <div key={index}>
              <div style={lbl}>
                {type} {typeSlots.length > 1 ? i + 1 : ''} <span style={{ color: '#554433' }}>({size})</span>
              </div>
              <select
                value={slotEquipment[index] ?? ''}
                onChange={(e) => setSlot(index, e.target.value)}
                style={sel}
              >
                <option value="">None</option>
                {options.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          );
        })
      )}

      <button onClick={apply} disabled={overPower} style={{ ...btnStyle(overPower ? '#333' : '#2a6a2a'), width: '100%', marginTop: 4, opacity: overPower ? 0.4 : 1, cursor: overPower ? 'not-allowed' : 'pointer' }}>
        {overPower ? 'Over Power Limit' : 'Apply Ship'}
      </button>
      <button
        onClick={() => { networkManager.sendDevKill(); setStatus('Dying…'); setTimeout(() => setStatus(''), 1500); }}
        style={{ ...btnStyle('#aa2222'), width: '100%', marginTop: 4 }}
      >
        Die
      </button>
      {status && (
        <div style={{ marginTop: 3, fontSize: 9, color: '#aaa' }}>{status}</div>
      )}
    </>
  );
}

// ==================== TAB 3: Teleport ====================

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: '#1a1208',
  color: '#e8c98a',
  border: '1px solid #6a3f1a',
  padding: '3px 5px',
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11,
  marginBottom: 2,
  boxSizing: 'border-box',
};

function TeleportTab() {
  const [x, setX] = useState<string>('0');
  const [y, setY] = useState<string>('0');
  const [status, setStatus] = useState<string>('');
  const [initialized, setInitialized] = useState(false);

  // Pre-fill with current player position
  useEffect(() => {
    if (initialized) return;
    const ps = networkManager.getDevPlayerState();
    if (!ps) return;
    setX(Math.round(ps.x).toString());
    setY(Math.round(ps.y).toString());
    setInitialized(true);
  }, [initialized]);

  // Compute constellation bounds
  const constellationCoords = networkManager.getConstellationCoords();
  const halfSize = WORLD_CONFIG.CONSTELLATION_SIZE / 2;
  const centerX = constellationCoords.x * WORLD_CONFIG.CONSTELLATION_SIZE;
  const centerY = constellationCoords.y * WORLD_CONFIG.CONSTELLATION_SIZE;
  const minX = centerX - halfSize;
  const maxX = centerX + halfSize;
  const minY = centerY - halfSize;
  const maxY = centerY + halfSize;

  const nx = parseFloat(x);
  const ny = parseFloat(y);
  const validX = !isNaN(nx) && nx >= minX && nx <= maxX;
  const validY = !isNaN(ny) && ny >= minY && ny <= maxY;
  const outOfBounds = !validX || !validY;

  const teleport = () => {
    if (isNaN(nx) || isNaN(ny)) {
      setStatus('Invalid coordinates');
      setTimeout(() => setStatus(''), 1500);
      return;
    }
    if (outOfBounds) return;
    networkManager.sendDevTeleport(nx, ny);
    setStatus(`Teleported to ${nx}, ${ny}`);
    setTimeout(() => setStatus(''), 1500);
  };

  const invalidInputStyle = { ...inputStyle, border: '1px solid #aa3322' };

  return (
    <>
      <div style={{ fontSize: 9, color: '#555', marginBottom: 4 }}>
        Bounds: X [{minX}, {maxX}] Y [{minY}, {maxY}]
      </div>

      <div style={lbl}>X Coordinate</div>
      <input
        type="number"
        value={x}
        onChange={(e) => setX(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && teleport()}
        style={validX ? inputStyle : invalidInputStyle}
      />
      <div style={lbl}>Y Coordinate</div>
      <input
        type="number"
        value={y}
        onChange={(e) => setY(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && teleport()}
        style={validY ? inputStyle : invalidInputStyle}
      />
      <button
        onClick={teleport}
        disabled={outOfBounds}
        style={{ ...btnStyle(outOfBounds ? '#333' : '#2a4a6a'), width: '100%', marginTop: 4, opacity: outOfBounds ? 0.4 : 1, cursor: outOfBounds ? 'not-allowed' : 'pointer' }}
      >
        {outOfBounds ? 'Out of Bounds' : 'Teleport'}
      </button>
      {status && (
        <div style={{ marginTop: 3, fontSize: 9, color: '#aaa' }}>{status}</div>
      )}
    </>
  );
}

// ==================== Main Panel with Tabs ====================

type TabId = 'spawner' | 'myship' | 'teleport';

export function DevSpawnerPanel() {
  if (!ENABLED) return null;

  const [activeTab, setActiveTab] = useState<TabId>('spawner');

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    background: active ? '#2a1a08' : '#0e0a04',
    color: active ? '#ffaa44' : '#665533',
    border: 'none',
    borderBottom: active ? '2px solid #ffaa44' : '2px solid transparent',
    padding: '4px 6px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10,
    cursor: 'pointer',
    fontWeight: active ? 'bold' : 'normal',
  });

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 12,
        left: 12,
        background: 'rgba(20, 14, 6, 0.9)',
        border: '1px solid #b86a2a',
        borderRadius: 4,
        padding: 0,
        color: '#e8c98a',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11,
        zIndex: 50,
        minWidth: 240,
        maxHeight: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
      }}
    >
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid #6a3f1a', flexShrink: 0 }}>
        <button style={tabStyle(activeTab === 'spawner')} onClick={() => setActiveTab('spawner')}>
          Spawner
        </button>
        <button style={tabStyle(activeTab === 'myship')} onClick={() => setActiveTab('myship')}>
          My Ship
        </button>
        <button style={tabStyle(activeTab === 'teleport')} onClick={() => setActiveTab('teleport')}>
          Teleport
        </button>
      </div>

      {/* Tab content */}
      <div style={{ padding: '8px 10px', overflowY: 'auto', flex: 1 }}>
        {activeTab === 'spawner' && <DummySpawnerTab />}
        {activeTab === 'myship' && <MyShipTab />}
        {activeTab === 'teleport' && <TeleportTab />}
      </div>
    </div>
  );
}
